// External
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Link } from 'react-router-dom';

// Internal
import NavBaseTemplate from '../templates/NavBaseTemplate';
import FeedbackTile from '../theme/modals/FeedbackTile';
import ArrowDatePicker from '../theme/forms/ArrowDatePicker';
import RowCard from '../cards/rowCard';
import Input from '../theme/forms/Input';
import {
  getSchedule,
  pullCampaignCanvassers,
  pullCampaignOffices,
  pullCampaignSiteCanvasses,
  pullCrews,
  pullSiteRoutes,
} from '../../store/api/calls';

class OverviewScreen extends Component {
  constructor(props) {
    super(props);

    this.state = {
      crews: [],
      date: new moment(),
      loading: false,
      selectedOffice: 0,
      groupBy: 'shifts',
      shifts: {},
      canvasses: {},
      canvasserSelectOptions: [{ value: 0, display: 'All Assigned' }],
      selectedCanvasser: 0,
      allExpanded: false,
    };

    this.SUBMIT_DATE_FORMAT = 'YYYY-MM-DD';
    this.DISPLAY_FORMAT = 'hh:mma';
    this.pastLimit = new moment().subtract(2, 'weeks');
    this.futureLimit = new moment().add(2, 'weeks');

    this.dateChangeTimeout = null;
  }

  componentDidMount() {
    this.setState({ loading: true });
    let requests = [];

    if (!this.props.offices.length) {
      requests.push(
        new Promise((resolve) => {
          pullCampaignOffices().then(() =>
            this.setState(
              {
                selectedOffice: this.props.offices[0].id,
              },
              resolve
            )
          );
        })
      );
    } else {
      requests.push(
        new Promise((resolve) => {
          this.setState(
            {
              selectedOffice: this.props.offices[0].id,
            },
            resolve
          );
        })
      );
    }

    if (!this.props.siteCanvasses.length) {
      requests.push(pullCampaignSiteCanvasses());
    }

    if (!this.props.canvassers.length) {
      requests.push(pullCampaignCanvassers());
    }

    // get data from any previous requests that may not be present in props first
    if (requests.length) {
      Promise.all(requests)
        .then((data) => {
          this.buildData();
        })
        .catch((err) => this.setState({ loading: false }));
    } else {
      this.buildData();
    }
  }

  /**
   * buildData()
   * - get all of the crews for a given day, and then assemble them with each of their associated siteroutes.
   */
  buildData = () => {
    // state will not be loading when changing the date
    if (!this.state.loading) this.setState({ loading: true });

    // TODO: make sure that office filtering will work and make the office id access safer.
    let params = {
      sitecanvass: this.props.siteCanvasses
        .filter((s) => s.office === this.state.selectedOffice)
        .map((sc) => sc.id)
        .join('|'),
      date: this.state.date.format(this.SUBMIT_DATE_FORMAT),
    };

    console.log('PARAMS', params);

    // TODO: this will also need to support crews for doors once all of that is built out.
    Promise.all([
      pullCrews(params),
      pullSiteRoutes(params),
      getSchedule(this.state.selectedOffice, this.state.date.format(this.SUBMIT_DATE_FORMAT)),
    ])
      .then((res) => {
        let crews = [];
        res[0].forEach((crew) => {
          crew.siteroute = res[1].find((sr) => sr.siteroute_id === crew.siteroute);
          crew.shift = res[2].find((s) => s.id === crew.shift);
          crews.push(crew);
        });

        let canvasses = {};
        let shifts = {};
        let options = [...this.state.canvasserSelectOptions];

        crews.forEach((crew) => {
          let sitecanvass =
            this.props.siteCanvasses.find((sc) => sc.id === crew.siteroute.sitecanvass_id) || {};
          crew.office = sitecanvass.office;
          crew.canvass = sitecanvass.name; // TODO: this will need to be expanded for doors support
          crew.canvassers.forEach((c) => {
            c.canvasser = this.props.canvassers.find(
              (canvasser) => c.canvasser_id === canvasser.id
            );
            if (!options.find((o) => o.value === c.canvasser_id)) {
              options.push({
                value: c.canvasser_id,
                display: c.canvasser.firstname + ' ' + c.canvasser.lastname,
              });
            }
          });

          if (!canvasses[crew.siteroute.sitecanvass_id]) {
            canvasses[crew.siteroute.sitecanvass_id] = {
              title: sitecanvass.name,
              data: [crew],
            };
          } else if (canvasses[crew.siteroute.sitecanvass_id].data) {
            canvasses[crew.siteroute.sitecanvass_id].data.push(crew);
          }

          if (!shifts[crew.shift.id]) {
            let start = new moment(`${crew.shift.shift_date} ${crew.shift.start_time}`).format(
              this.DISPLAY_FORMAT
            );
            let end = new moment(`${crew.shift.shift_date} ${crew.shift.end_time}`).format(
              this.DISPLAY_FORMAT
            );

            shifts[crew.shift.id] = {
              title: `${start} - ${end}`,
              data: [],
            };
            shifts[crew.shift.id].data.push(crew);
          } else if (shifts[crew.shift.id].data) {
            shifts[crew.shift.id].data.push(crew);
          }
        });

        this.setState({
          crews,
          shifts,
          canvasses,
          loading: false,
          canvasserSelectOptions: options,
        });
      })
      .catch((err) => this.setState({ loading: false }));
  };

  componentWillUnmount() {
    clearTimeout(this.dateChangeTimeout);
  }

  handleDateChange = (date) => {
    // set a timeout to do the call to avoid making repeated calls when rapidly changing the date.
    if (!this.dateChangeTimeout) {
      this.dateChangeTimeout = setTimeout(this.buildData, 400);
    } else {
      clearTimeout(this.dateChangeTimeout);
      this.dateChangeTimeout = setTimeout(this.buildData, 400);
    }

    this.setState({
      date,
    });
  };

  sortCards(data) {
    if (this.state.groupBy === 'canvasses') {
      // if canvasses, organize chronologically
      return data.sort((a, b) => {
        return (
          new moment(
            `${a.shift.shift_date} ${a.shift.start_time}`,
            'YYYY-MM-DD hh:mm:ss'
          ).valueOf() -
          new moment(`${b.shift.shift_date} ${b.shift.start_time}`, 'YYYY-MM-DD hh:mm:ss').valueOf()
        );
      });
    } else {
      // if ordered by shift, organize alpha asc
      return data.sort((a, b) => {
        return a.siteroute.siteroute_name.localeCompare(b.siteroute.siteroute_name);
      });
    }
  }

  filterCards(data) {
    data = data.filter((crew) => crew.office === +this.state.selectedOffice);

    if (this.state.selectedCanvasser) {
      data = data.filter((crew) =>
        crew.canvassers.find((c) => c.canvasser_id === this.state.selectedCanvasser)
      );
    }

    return data;
  }

  renderCards() {
    let display = this.state[this.state.groupBy];
    let result = [];
    let sortedKeys;
    let expanded = false;

    // organize the object keys chronologically or alpha asc so the titles for each section of card are sorted.
    if (this.state.groupBy === 'shifts') {
      sortedKeys = Object.keys(display).sort((a, b) => {
        return (
          new moment(
            `${display[a].data[0].shift.shift_date} ${display[a].data[0].shift.start_time}`,
            'YYYY-MM-DD hh:mm:ss'
          ).valueOf() -
          new moment(
            `${display[b].data[0].shift.shift_date} ${display[b].data[0].shift.start_time}`,
            'YYYY-MM-DD hh:mm:ss'
          ).valueOf()
        );
      });
    } else {
      sortedKeys = Object.keys(display).sort((a, b) => {
        return display[a].data[0].siteroute.siteroute_name.localeCompare(
          display[b].data[0].siteroute.siteroute_name
        );
      });
    }

    sortedKeys.forEach((key) => {
      let filteredCards = this.filterCards(display[key].data);
      let sortedCards = this.sortCards(filteredCards);

      if (!sortedCards.length) {
        return <RowCard columns={[{ text: 'Nothing Scheduled for this Office Today' }]} />;
      }

      result.push(<h4 key={btoa(`${Math.random()}` + key)}>{display[key].title}</h4>);

      sortedCards.forEach((crew) => {
        // Children are sites and assignedSites in each sitecanvass that will be rendered by the parent card
        let children = crew.siteroute.routeseats.map((rs, i) => {
          let sites = rs.sites.map((s, i) => (
            <div
              className={'seat-site'}
              key={btoa(`${s.site_name}_${i}`)}
              style={{
                maxHeight: '10em',
                overflow: 'scroll',
                display: 'block',
                border: `1px solid ${crew.siteroute.hex_color}`,
                fontSize: '12px',
                textAlign: 'center',
                overflowX: 'hidden',
                lineHeight: '14px',
                margin: '1em .2em .2em .4em',
                padding: '.3em .8em',
                opacity: '.8',
              }}
            >
              {s.site_name}
            </div>
          ));

          let crewcanvasser = crew.canvassers.find((cc) => cc.routeseat_id === rs.routeseat_id);
          let canvasser =
            crewcanvasser && this.props.canvassers.find((c) => c.id === crewcanvasser.canvasser_id);

          if (canvasser) {
            if (!expanded) {
              // only set it up once if true, otherwise later assignedSites will close the card again.
              expanded = canvasser.id === this.state.selectedCanvasser;
            }

            return (
              <div
                className={'seat-site-list'}
                key={btoa(`${rs.routeseat_id}`) + i}
                style={{
                  maxHeight: '12em',
                  overflow: 'scroll',
                  display: 'inline-block',
                  verticalAlign: 'text-top',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  lineHeight: '14px',
                  width: '250px',
                  margin: '1em .4em .4em .1em',
                }}
              >
                {canvasser.firstname} {canvasser.lastname}
                {sites}
              </div>
            );
          } else {
            return null;
          }
        });

        let sitecanvass =
          this.props.siteCanvasses.find((sc) => sc.id === crew.siteroute.sitecanvass_id) || {};

        result.push(
          <RowCard
            expanded={expanded || this.state.allExpanded}
            key={btoa(`${crew.id}`)}
            columns={[
              {
                text: crew.siteroute.siteroute_name,
                style: {
                  fontWeight: 'bold',
                },
              },
              {
                icon: <i className="fas fa-map-marker-alt" />,
                text: sitecanvass.name,
              },
              {
                text: `Canvassers: ${crew.canvassers.length}`,
              },
              {
                text: `Seats: ${crew.siteroute.routeseats.length}`,
              },
              {
                text: `${new moment(`${crew.shift.shift_date} ${crew.shift.start_time}`).format(
                  this.DISPLAY_FORMAT
                )} - ${new moment(`${crew.shift.shift_date} ${crew.shift.end_time}`).format(
                  this.DISPLAY_FORMAT
                )}`,
              },
              {
                icon: (
                  <Link to={`/sitecanvass/${sitecanvass.id}/schedule`}>
                    <i className="fas fa-edit fa-pull-right" title="Go To Scheduler" />
                  </Link>
                ),
              },
            ]}
            color={crew.siteroute.hex_color}
            children={children}
          />
        );
      });
    });

    if (!result.length && !this.state.loading) {
      return <RowCard columns={[{ text: 'Nothing Scheduled for this Office Today' }]} />;
    }

    return result;
  }

  render() {
    // TODO: render a placeholder card for loading or no data.
    return (
      <NavBaseTemplate location={this.props.location} showAdmin={this.props.is_admin}>
        <div className="flx space_between">
          <h3 className={'primary-text'}>Schedule Overview</h3>
          <ArrowDatePicker
            onClick={this.handleDateChange}
            date={this.state.date}
            dateFormat={this.SUBMIT_DATE_FORMAT}
            backDisabled={this.state.date.isSame(this.pastLimit)}
            forwardDisabled={this.state.date.isSame(this.futureLimit)}
            style={{ marginRight: '3em' }}
          />
        </div>
        <div className="flx space_between">
          <div className={'page-top-inputs'}>
            <div className="page-top-input">
              <Input
                title="Office"
                onChange={(val) => {
                  this.setState({ selectedOffice: +val }, () => this.buildData());
                }}
                value={this.state.selectedOffice}
                type="select"
                options={this.props.offices.map((o) => ({ value: o.id, display: o.name }))}
              />
            </div>
            <div className="page-top-input">
              <Input
                title="Group By"
                onChange={(val) => this.setState({ groupBy: val })}
                value={this.state.groupBy}
                type="select"
                options={[
                  { value: 'shifts', display: 'Shift' },
                  {
                    value: 'canvasses',
                    display: 'Canvass',
                  },
                ]}
              />
            </div>
            <div className="page-top-input">
              <Input
                title="Filter By Canvasser"
                onChange={(val) => this.setState({ selectedCanvasser: +val })}
                value={this.state.selectedCanvasser}
                type="select"
                options={this.state.canvasserSelectOptions}
              />
            </div>
          </div>
          <div style={{ marginRight: '1em', minWidth: '9.3em' }}>
            <Input
              title="Expand All"
              onChange={(val) => this.setState({ allExpanded: !this.state.allExpanded })}
              value={this.state.allExpanded}
              type="switch-left"
            />
          </div>
        </div>
        {this.renderCards()}
        <FeedbackTile
          text={
            <div>
              <i className="fas fa-asterisk fa-spin" style={{ marginRight: '15px' }} />
              Loading...
            </div>
          }
          visible={this.state.loading}
        />

        {/*use feedback tile icon, text, state*/}
        <FeedbackTile
          text={
            <div>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '15px' }} />
              There was a server error.
            </div>
          }
          visible={false}
          backgroundColor={'red'}
        />
      </NavBaseTemplate>
    );
  }
}

const mapStateToProps = (state) => ({
  campaigns: state.session.campaigns,
  focusedCampaign: state.session.focusedCampaign,
  is_admin: state.permissions.is_admin,
  canvassers: state.canvassers.data,
  crews: state.crews.data,
  offices: state.offices.data,
  siteCanvasses: state.siteCanvasses.data,
  siteRoutes: state.siteRoutes.data,
});

export default connect(mapStateToProps)(OverviewScreen);
